import React from 'react'
import { NavLink, useRouteMatch } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { CheckRounded } from '@material-ui/icons'
import clsx from 'clsx'

import { image } from '../../../../lib/image'

import { STEPS, stepNumber } from '../steps'
import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// THE MARK. Same artwork and same drawn height as components/header/index.js
// and components/footer/index.js: 7942 x 1395 (Pacifico, outlined), aspect
// 5.693, drawn 24px high, so 137 x 24. The attributes reserve the box before
// the bytes land (DESIGN.md §3) and they are the aspect ratio, so they have to
// agree with the drawn height in styles.js or the mark is stretched. It was
// 163 x 32 here, built for the serif mark it replaced, which drew it a third
// too big at the wrong aspect. Swap the mark again and all four places change:
// MARK in components/header/index.js, the footer <img>, the image_tag in
// app/views/gwmc/_hero.html.haml, and this.
const MARK = { width: 137, height: 24 }

const STEP_LABELS = ['Wash', 'Details', 'Extras', 'Checkout']
// book-and-pay arm: two steps, the second is the whole booking.
const BOOK_LABELS = ['Wash', 'Book & pay']

export const BookingStepper = React.memo(({ logo }) => {
  const classes = useStyles()
  const step = useRouteMatch('/booking/:step')?.params?.step || STEPS.SERVICE
  const activeIndex = stepNumber[step] ?? 0
  const isBook = step === STEPS.BOOK
  const labels = isBook ? BOOK_LABELS : STEP_LABELS

  const progressPct = ((activeIndex + 1) / labels.length) * 100

  return (
    <div className={classes.bar}>
      <div className={classes.inner}>
        {logo && (
          // The booking screen renders no site header (the header component
          // returns null on /booking/:step), so this mark is the only brand on
          // the screen and the only way back to the site. `exact` or it carries
          // aria-current="page" on every booking route.
          <NavLink exact to="/" className={classes.logoLink} aria-label="GoWashMyCar, home">
            <img
              className={classes.logoImg}
              src={image('./wordmark.svg')}
              alt="GoWashMyCar"
              width={MARK.width}
              height={MARK.height}
            />
          </NavLink>
        )}

        {/* Book & pay is one screen: logo only. "STEP 2 OF 2" and the
            "Wash / Book & pay" labels looked tappable and said nothing the
            screen does not (booking-screen-trim, 9 Oct 2026). */}
        {!isBook && (<>
        {/* Desktop: numbered dots + connectors */}
        <div className={classes.steps}>
          {labels.map((label, i) => {
            const isDone = i < activeIndex
            const isCurrent = i === activeIndex
            return (
              <React.Fragment key={label}>
                <div className={classes.stepGroup}>
                  <div className={clsx(
                    classes.stepDot,
                    isDone && classes.stepDotDone,
                    isCurrent && classes.stepDotCurrent
                  )}>
                    {isDone ? <CheckRounded style={{ fontSize: 16 }} /> : i + 1}
                  </div>
                  <span className={clsx(
                    classes.stepLabel,
                    isDone && classes.stepLabelDone,
                    isCurrent && classes.stepLabelCurrent
                  )}>
                    {label}
                  </span>
                </div>
                {i < labels.length - 1 && (
                  <div className={clsx(classes.connector, isDone && classes.connectorDone)} />
                )}
              </React.Fragment>
            )
          })}
        </div>

        {/* Mobile: step counter + progress bar */}
        <div className={classes.mobile}>
          <div className={classes.mobileLine}>
            <span className={classes.mobileStep}>Step {activeIndex + 1} of {labels.length}</span>
            <span className={classes.mobileTitle}>{labels[activeIndex]}</span>
          </div>
          <div className={classes.mobileBar}>
            <div className={classes.mobileBarFill} style={{ width: `${progressPct}%` }} />
          </div>
        </div>
        </>)}
      </div>
    </div>
  )
})
